    <template>
    <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
            <div class="card-icon icon-style">
                <md-icon>payments</md-icon>
                {{ title }}
            </div>
        </md-card-header>
        <div class="has-invoice" v-if="hasInvoice">
            <md-icon class="invoice-icon-color">task</md-icon>
            <i>*Costs cannot be changed because job has been invoiced to a team member</i>
        </div>
        <div class="is-approved" v-if="isCostsApproved">
            <md-icon class="invoice-icon-color">task</md-icon>
            <i>*Costs cannot be changed because job has been approved</i>
        </div>
        <md-card-content>
            <div v-if="updatingRates">
                <loading-component-spinner loading-text="Rates Updating..." />
            </div>
            <p v-if="combinedCostLines.length === 0 && !updatingRates" class="grayedout-text text-center">
                No {{ title }} found
            </p>
            <div v-if="!updatingRates && combinedCostLines.length > 0">
                <md-table>
                    <md-table-row slot="header">
                        <md-table-head class="drag-icon"></md-table-head>
                        <md-table-head>Description</md-table-head>
                        <md-table-head>Unit</md-table-head>
                        <md-table-head>Amount</md-table-head>
                        <md-table-head v-if="$root.isDesktop && !isReadOnlyUser">
                            Actions
                        </md-table-head>
                    </md-table-row>

                    <div v-if="updatingRates">
                        <loading-component-spinner loading-text="Rates Updating..." />
                    </div>
                    <md-table-row
                        v-if="!updatingRates" v-for="(rate, index) in combinedCostLines"
                        :key="rate.id"
                        :class="{
                            'editable-input': editingIndex === index,
                            'readonly-row': editingLoadingIndex === index
                        }"
                    >
                        <rate-charge-rule 
                            :rule-data="getChargeRuleData()"
                            :currency="getCurrency"
                            :rate="rate"
                            :rate-index="index"
                        />
                        <md-table-cell class="drag-icon">
                            <md-icon v-if="rate.isManualCharge || rate.manuallyChanged">
                                <span class="">
                                    drive_file_rename_outline
                                </span>
                            </md-icon>
                        </md-table-cell>
                        <md-table-cell class="description-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="editBuffer.description"
                                        @input="onEditChange"
                                        type="text"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{ rate.description }}
                            </template>
                        </md-table-cell>
                        <md-table-cell v-if="rate.type === 'hourly'" class="unitcharge-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="originalEditBuffer.timeInput"
                                        @input="onEditChange"
                                        type="text"
                                        :class="[
                                            'md-input editable-value',
                                            {
                                                'invalid-time-format': editingIndex !== null && combinedCostLines[editingIndex].type === 'hourly'
                                                    && originalEditBuffer.timeInput && !$_validateHourlyTimeFormat(originalEditBuffer.timeInput)
                                            }
                                        ]"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                               {{ rate.chargeUnit }}
                            </template>
                        </md-table-cell>
                        <md-table-cell v-else :class="['unitcharge-container', { 'readonly-fuel-levy': rate.type === 'fuelLevy' || rate.type === 'surcharge' || rate.type === 'commission' }]">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="originalEditBuffer.chargeUnit"
                                        @input="onEditChange"
                                        type="number"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{ rate.chargeUnit }}
                            </template>
                        </md-table-cell>
                        <md-table-cell class="amount-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model.number="editBuffer.amount"
                                        @input="onEditChange"
                                        type="number"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{
                                    $_getRateAmount(rate)
                                        | currency(getCurrency)
                                }}
                            </template>
                        </md-table-cell>
                        <md-table-cell class="action-buttons">
                            <template v-if="editingIndex === index">
                                <template v-if="editingLoadingIndex === index">
                                    <md-progress-spinner md-mode="indeterminate" :md-diameter="23" />
                                </template>
                                <template v-else>
                                    <md-button
                                        :disabled="disableSaveButton()"
                                        class="md-success md-just-icon md-round"
                                        @click="saveEdit"
                                    >
                                        <md-icon>save</md-icon>
                                    </md-button>
                                    <md-button class="md-danger md-just-icon md-round" @click="cancelEdit">
                                        <md-icon>cancel</md-icon>
                                    </md-button>
                                </template>
                            </template>
                            <template v-else>
                                <md-button
                                    v-if="
                                        (!isCostsApproved && !hasInvoice && !isReadOnlyUser && !rate.isService) ||
                                            (hasInvoice &&
                                                invoiceStatus() === invoiceStatusAddNewLines &&
                                                rate.type === 'draftCharge')
                                    "
                                    title="Edit"
                                    class="md-success md-just-icon md-round"
                                    @click.stop="startEdit(index, rate)"
                                >
                                    <md-icon>edit</md-icon>
                                </md-button>
                                <md-button
                                    v-if="
                                        (!isCostsApproved && !hasInvoice && !isReadOnlyUser && !rate.isService) ||
                                            (hasInvoice &&
                                                invoiceStatus() === invoiceStatusAddNewLines &&
                                                rate.type === 'draftCharge')
                                    "
                                    title="Remove"
                                    class="md-danger md-just-icon md-round"
                                    @click.stop="deleteCostLine(rate)"
                                >
                                    <md-icon>delete</md-icon>
                                </md-button>
                            </template>
                        </md-table-cell>
                    </md-table-row>
                    <md-table-row v-if="!updatingRates">
                        <md-table-cell colspan="3" />
                        <md-table-cell class="total-style">
                            {{
                                $_getTotalAmount(combinedCostLines)
                                    | currency(getCurrency)
                            }}
                        </md-table-cell>
                        <md-table-cell />
                    </md-table-row>
                </md-table>
            </div>
        </md-card-content>
        <md-card-actions
            v-if="!isCostsApproved && !hasInvoice && !isReadOnlyUser"
            :class="{ 'is-loading-rates': updatingRates }"
        >
            <md-button
                class="md-warning revert-button"
                @click="validateRevertingCosts()"
            >
                Revert to Default Costs
            </md-button>
            <md-button class="md-success" @click="handleAddNewCost()">
                Add Cost
            </md-button>
        </md-card-actions>
        <md-card-actions v-else-if="invoiceStatus() === invoiceStatusAddNewLines">
            <add-new-line-to-draft-invoice
                class="add-line"
                :invoice-id="getInvoiceId()"
                title="Cost"
                :stop-id="stopDetails ? stopDetails.stopId : null"
                :shipment-id="shipmentDetails ? shipmentDetails.shipmentId : null"
                :run-id="runDetails ? runDetails.runId : null"
                @lineCreated="addNewCostLine"
            />
        </md-card-actions>
    </md-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { FeatureManager } from '@/directives';
import { FEATURE_NAMES } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
// eslint-disable-next-line import/no-cycle
import AddNewLineToDraftInvoice from './AddNewLineToDraftInvoice';
import CostItemModal from './CostItemModal';
import LoadingComponentSpinner from './LoadingComponentSpinner';
import RateChargeRule from './RateChargeRule';

export default {
    name: 'CostRatesList',
    components: {
        LoadingComponentSpinner,
        AddNewLineToDraftInvoice,
        RateChargeRule
    },
    mixins: [GeneralMixin, RatesEngineMixin, FeatureManager],
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser',
            isIndividualUser: 'user/isIndividualUser',
            user: 'user/user'
        }),
        combinedCostLines() {
            const filteredServiceLines = this.serviceLines.filter(
                (s) => s.unitPriceExTax != null && s.unitPriceExTax !== 0 && s.quantity != null
            );
            return this.costLines.concat(filteredServiceLines);
        },
        getCurrency() {
            return this.shipmentDetails?.currency ?? this.stopDetails?.currency ?? this.runDetails?.currency ?? "AUD";     
        }
    },
    props: {
        costLines: {
            type: Array,
            default: () => []
        },
        shipmentDetails: {
            type: Object,
            default: () => null
        },
        stopDetails: {
            type: Object,
            default: () => null
        },
        title: {
            type: String,
            default: null
        },
        hasInvoice: {
            type: Boolean,
            default: false
        },
        ratesHaveChanged: {
            type: Boolean,
            default: false
        },
        serviceLines: {
            type: Array,
            default: () => []
        },
        runDetails: {
            type: Object,
            default: () => null
        }
    },
    data() {
        return {
            updatingRates: false,
            isCostsApproved: false,
            invoiceStatusAddNewLines: 'Draft',
            editingIndex: null,
            editingLoadingIndex: null,
            editBuffer: {
                description: '',
                amount: 0,
                chargeUnit: null,
                timeInput: null
            },
            originalEditBuffer: {
                description: '',
                amount: 0,
                chargeUnit: null,
                timeInput: null
            },
            showEditActions: false,
            originalChargeUnit: null,
            trimmedUnit: null,
            validationTimeout: null
        };
    },
    watch: {
        ratesHaveChanged(newValue, oldValue) {
            this.updatingRates = newValue;
        }
    },
    mounted() {
        if (this.$_hasFeature(FEATURE_NAMES.JobsCostApproval)) {
            this.isCostsApproved = this.shipmentDetails
                ? this.shipmentDetails.isCostsApproved
                : this.stopDetails?.isCostsApproved ?? this.runDetails?.isCostsApproved ?? false;
        }
    },
    beforeDestroy() {
        // Clean up validation timeout when component is destroyed
        if (this.validationTimeout) {
            clearTimeout(this.validationTimeout);
            this.validationTimeout = null;
        }
    },
    methods: {
        deleteCostLine(cost) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Delete Cost',
                    body: 'Are you sure you want to delete this cost?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.isListLoading = true;
                        await this.handleDeleteCost(cost.id);
                    }
                });
        },
        async handleDeleteCost(id) {
            const payload = {
                method: 'delete'
            };
            const api = `/api/rates/cost/delete/${id}`;
            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Cost was deleted!',
                    type: 'success'
                });
            } catch (e) {
                const message = 'Error in deleting a cost.';
                showErrorMessage(this, message, e);
            } finally {
                this.emitUpdate();
            }
        },
        handleAddNewCost() {
            this.$modal
                .show(CostItemModal, {
                    shipmentId: this.shipmentDetails?.shipmentId,
                    stopId: this.stopDetails?.stopId,
                    isManualCharge: true,
                    manuallyChanged: false,
                    runId: this.runDetails?.runId,
                    costLine: {
                        description: null,
                        amount: 0,
                        chargeUnit: null,
                        quantity: 1
                    }
                })
                .then((response) => {
                    this.emitUpdate();
                    this.$modal.hide();
                });

            this.postSave();
        },
        validateRevertingCosts() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Revert Costs',
                    body: 'Are you sure you want to revert costs? <br /><br /> This cannot be undone.',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        await this.handleRevertToDefaultCosts();
                    }
                });
        },
        async handleRevertToDefaultCosts() {
            try {
                this.$_handleLoaderState(true, 'REVERTING...');

                const data = {
                    isCosting: true,
                    isCharging: false,
                    revertCosts: true,
                    revertCharges: false
                };

                const payload = {
                    method: 'post',
                    data
                };

                let endpoint = null;

                if (this.shipmentDetails?.shipmentId) {
                    endpoint = `/api/rates/shipment/${this.shipmentDetails.shipmentId}/generate-rates`;
                } else if (this.stopDetails?.stopId) {
                    endpoint = `/api/rates/stop/${this.stopDetails.stopId}/generate-rates`;
                } else if (this.runDetails?.runId) {
                    endpoint = `/api/rates/run/${this.runDetails.runId}/generate-rates`;
                }

                await handleRequests(endpoint, payload);

                this.$notify({
                    message: 'Costs Reverted',
                    type: 'success'
                });
            } catch (error) {
                const message = 'Error Reverting Costs';
                showErrorMessage(this, message, null);
            } finally {
                this.postSave();
                this.ratesIsLoading = false;
                this.$_handleLoaderState(false);
                this.emitUpdate();
            }
        },
        emitUpdate() {
            if (this.shipmentDetails?.shipmentId) {
                this.$emit('updateShipmentDetails');
            }

            if (this.stopDetails?.stopId) {
                this.$emit('updateStopDetails');
            }

            if (this.runDetails?.runId) {
                this.$emit('updateRunDetails');
            }
        },
        getInvoiceId() {
            if (this.stopDetails && this.stopDetails.teamMemberInvoiceId != null) {
                return this.stopDetails.teamMemberInvoiceId;
            }

            if (this.shipmentDetails && this.shipmentDetails.teamMemberInvoiceId != null) {
                return this.shipmentDetails.teamMemberInvoiceId;
            }

            if (this.runDetails && this.runDetails.teamMemberInvoiceId != null) {
                return this.runDetails.teamMemberInvoiceId;
            }

            return null;
        },
        invoiceStatus() {
            if (this.stopDetails && this.stopDetails.teamMemberInvoiceStatus != null) {
                return this.stopDetails.teamMemberInvoiceStatus;
            }

            if (this.shipmentDetails && this.shipmentDetails.teamMemberInvoiceStatus != null) {
                return this.shipmentDetails.teamMemberInvoiceStatus;
            }

            if (this.runDetails && this.runDetails.teamMemberInvoiceStatus != null) {
                return this.runDetails.teamMemberInvoiceStatus;
            }

            return null;
        },
        addNewCostLine(costLine) {
            const mappedCostLine = {
                id: costLine.id,
                description: costLine.description,
                quantity: costLine.quantity,
                unitPriceExTax: costLine.unitPriceExTax,
                isManualCharge: costLine.isManualCharge,
                manuallyChanged: costLine.manuallyChanged,
                type: costLine.type
            };

            this.costLines.push(mappedCostLine);
            this.postSave();
        },
        getChargeRuleData() {
            const rule = this.shipmentDetails?.shipmentAppliedCostingRule ?? this.stopDetails?.stopAppliedCostingRule;
            
            if (rule == null || rule.ruleType == null)
                return null;

            return rule;
        },
        startEdit(index, rate) {
            this.$_startEdit(index, rate, this.editBuffer, this.originalEditBuffer);
        },
        onEditChange() {
            this.showEditActions = true;
            
            // Clear any existing timeout
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout);
            }
            
            // Validate hourly time format if editing an hourly rate with debounce
            if (this.editingIndex !== null) {
                const rate = this.combinedCostLines[this.editingIndex];
                if (rate.type === 'hourly' && this.originalEditBuffer.timeInput) {
                    this.validationTimeout = setTimeout(() => {
                        const isValid = this.$_validateHourlyTimeFormat(this.originalEditBuffer.timeInput);
                        if (!isValid) {
                            this.$notify({
                                message: 'Invalid time format. Please use format like "4h 54m" or "2h 30m"',
                                type: 'warning'
                            });
                        }
                    }, 1000);
                }
            }
        },
        cancelEdit() {
            // Clear validation timeout when cancelling edit
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout);
                this.validationTimeout = null;
            }
            
            this.editingIndex = null;
            this.showEditActions = false;
        },
        disableSaveButton() {
            if (!this.showEditActions) {
                return false;
            }
            
            // Additional validation for hourly rates
            if (this.editingIndex !== null) {
                const rate = this.combinedCostLines[this.editingIndex];
                if (rate.type === 'hourly' && this.originalEditBuffer.timeInput) {
                    const canSave = this.$_validateHourlyTimeFormat(this.originalEditBuffer.timeInput);
                    return !canSave;
                }
            }
            
            return false;
        },
        postSave() {
            this.editingIndex = null;
            this.editingLoadingIndex = null;
            this.showEditActions = false;
        },
        async saveEdit() {
            let entityType;
            let entityId;
            
            if (this.shipmentDetails !== null) {
                entityType = 'Shipment';
                entityId = this.shipmentDetails.shipmentId;
            }
            else if (this.stopDetails !== null) {
                entityType = 'Stop';
                entityId = this.stopDetails.stopId;
            } 
            else if (this.runDetails !== null) {
                entityType = 'Run';
                entityId = this.runDetails.runId;
            }
            
            const result = this.$_saveEdit(
                this.combinedCostLines,
                this.editBuffer,
                this.originalEditBuffer,
                entityType,
                entityId,
                'Cost',
                this.isManualCharge
            );
            
            try {
                if (result && result.api) {
                    await handleRequests(result.api, result.payload);
                    this.$notify({
                        message: `${result.entityType} updated!`,
                        type: 'success'
                    });
                }
            } catch (e) {
                const message = `Cannot edit ${result?.entityType.toLowerCase()}.`;
                showErrorMessage(this, message, e);
            } finally {
                this.postSave();
                this.emitUpdate();
            }   
        }
    }
};
</script>

<style lang="scss" scoped>
.total-style {
    color: #4caf50 !important;
    font-weight: bold !important;
}

.icon-style {
    color: white;
}

.revert-button {
    margin-inline-end: auto !important;
}

.disabled-rates {
    pointer-events: none;
}

.is-loading-rates {
    margin-top: 150px !important;
}

.action-buttons {
    button,
    ::v-deep button {
        margin: 0 2px;
    }

    button:last-child {
        margin-right: 0;
    }
}

::v-deep .md-table-head-label {
    color: #4caf50 !important;
}

.has-invoice,
.is-approved {
    padding-top: 15px;
    padding-left: 20px;
}

.invalid-time-format {
    background-color: #ffebee !important;
}

.invoice-icon-color {
    color: #4caf50 !important;
}

.add-line {
    pointer-events: auto !important;
}


.edit-actions-bar {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
}

.md-table-cell .md-field {
  margin: 0;
  padding: 0;
}

.md-table-cell .md-input,
.md-table-cell input {
  width: 30px;
  font-size: inherit;
  line-height: inherit;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  box-shadow: none;
  min-width: 0;
  height: 25px;
  box-sizing: border-box;
}

.editable-input {
  background: #f0f0f0 !important;
  border-radius: 4px;
  padding: 10px 15px;
  transition: border 0.2s;
}

.editable-value {
    border-bottom: 2px solid #e0e0e0 !important;
}

.amount-container {
    width: 150px !important;
}

.description-container {
    width: 300px !important;
}

.row-updating {
  background: #f0f0f0 !important;
}

.edit-spinner {
  display: inline-block;
  vertical-align: middle;
  margin: 0 8px;
}

.unitcharge-container {
    width: 150px;
}

.readonly-row {
    pointer-events: none;
    opacity: 0.6; 
}

.readonly-fuel-levy {
    pointer-events: none;
    opacity: 0.8; 
}
</style>

